import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  TextField,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, useParams } from 'react-router-dom';
import CountryFlagLabel from '../../components/CountryFlagLabel';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import TypeaheadSelect from '../../components/TypeaheadSelect'; // 3.4.25: قوائم بحث-بالكتابة
import { formatDateInput } from '../../utils/dateUtils';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
} from '../../theme/smartStableTheme';

// T6: عبر services/api المركزي (baseURL + توكن تلقائيًا) بدل نسخة axios محلية.

const SectionCard = ({ title, children }) => (
  <Card elevation={0} sx={{ ...cardSx, mb: 2.5 }}>
    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
      <Typography
        variant="h6"
        sx={{ color: COLORS.text, fontWeight: 900, mb: 0.5 }}
      >
        {title}
      </Typography>
      <Divider sx={{ borderColor: COLORS.line, mb: 2 }} />
      <Grid container spacing={2}>
        {children}
      </Grid>
    </CardContent>
  </Card>
);

const HorseEdit = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { horseId } = useParams();
  const isRtl = String(i18n.language || '').startsWith('ar');

  const [form, setForm] = useState({
    name: '',
    nameArabic: '',
    fatherName: '',
    fatherNameArabic: '',
    motherName: '',
    motherNameArabic: '',
    breederName: '',
    breederNameArabic: '',
    breedId: '',
    rfidChipNumber: '',
    colorId: '',
    gender: '',
    dateOfBirth: '',
    ownerId: '',
    countryOfOriginId: '',
    registrationNumber: '',
    registrationDate: '',
    pedigreeHtml: '',
  });

  // المرفقات: ملف جديد (File) أو حذف القائم (boolean)
  const [newImage, setNewImage] = useState(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [newCert, setNewCert] = useState(null);
  const [removeExistingCert, setRemoveExistingCert] = useState(false);

  const [owners, setOwners] = useState([]);
  const [colors, setColors] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [countries, setCountries] = useState([]);

  const [horse, setHorse] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  // معاينة الصورة الجديدة قبل الرفع
  const newImagePreviewUrl = useMemo(
    () => (newImage ? URL.createObjectURL(newImage) : ''),
    [newImage]
  );
  useEffect(() => {
    return () => {
      if (newImagePreviewUrl) URL.revokeObjectURL(newImagePreviewUrl);
    };
  }, [newImagePreviewUrl]);

  useEffect(() => {
    if (!horseId) return;
    const fetchHorseData = async () => {
      setLoading(true);
      try {
        const { data: h } = await api.get(`/horses/${horseId}`);
        setHorse(h);
        setForm({
          name: h.name ?? '',
          nameArabic: h.name_arabic ?? '',
          fatherName: h.father_name ?? '',
          fatherNameArabic: h.father_name_arabic ?? '',
          motherName: h.mother_name ?? '',
          motherNameArabic: h.mother_name_arabic ?? '',
          breederName: h.breeder_name ?? '',
          breederNameArabic: h.breeder_name_arabic ?? '',
          breedId: h.breed_id ?? '',
          rfidChipNumber: h.rfid_chip_number ?? '',
          colorId: h.color_id ?? '',
          gender: h.gender ?? '',
          dateOfBirth: h.date_of_birth ? formatDateInput(h.date_of_birth) : '',
          ownerId: h.owner_id ?? '',
          countryOfOriginId: h.country_of_origin_id ?? '',
          registrationNumber: h.registration_number ?? '',
          registrationDate: h.registration_date
            ? formatDateInput(h.registration_date)
            : '',
          pedigreeHtml: h.pedigree_html ?? '',
        });
      } catch (err) {
        setError(t('error_loading_horse_data'));
      } finally {
        setLoading(false);
      }
    };
    fetchHorseData();
  }, [horseId, t]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ownersRes, colorsRes, breedsRes, countriesRes] =
          await Promise.all([
            api.get('/owners/summary'),
            api.get('/horse_colors'),
            api.get('/horse_breeds'),
            api.get('/countries'),
          ]);
        setOwners(ownersRes.data);
        setColors(colorsRes.data);
        setBreeds(breedsRes.data);
        setCountries(countriesRes.data);
      } catch (err) {
        console.error('Failed to fetch data: ' + err.message);
      }
    };
    fetchData();
  }, []);

  // هل توجد شهادة فعّالة (جديدة أو قائمة لم تُحذف)؟
  const hasActiveCert =
    Boolean(newCert) ||
    (Boolean(horse?.registration_certificate) && !removeExistingCert);

  const validateFields = () => {
    const errs = {};
    if (!form.name) errs.name = t('please_fill_horse_name');
    if (!form.nameArabic) errs.nameArabic = t('please_fill_horse_name_arabic');
    if (!form.gender) errs.gender = t('please_select_gender');
    if (!form.dateOfBirth) errs.dateOfBirth = t('please_select_date_of_birth');
    if (!hasActiveCert)
      errs.registrationCertificate = t(
        'please_upload_registration_certificate'
      );
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError(t('fix_errors_below'));
      return false;
    }
    setError('');
    return true;
  };

  const cleanText = (v) =>
    v === undefined || v === null || v === 'null' ? '' : v;
  const appendIntIfPresent = (fd, key, v) => {
    if (v !== '' && v !== undefined && v !== null && v !== 'null') {
      fd.append(key, String(v));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    setSubmitting(true);
    const formData = new FormData();
    formData.append('name', cleanText(form.name));
    formData.append('name_arabic', cleanText(form.nameArabic));
    formData.append('father_name', cleanText(form.fatherName));
    formData.append('father_name_arabic', cleanText(form.fatherNameArabic));
    formData.append('mother_name', cleanText(form.motherName));
    formData.append('mother_name_arabic', cleanText(form.motherNameArabic));
    formData.append('breeder_name', cleanText(form.breederName));
    formData.append('breeder_name_arabic', cleanText(form.breederNameArabic));

    appendIntIfPresent(formData, 'breed_id', form.breedId);
    appendIntIfPresent(formData, 'color_id', form.colorId);
    appendIntIfPresent(formData, 'owner_id', form.ownerId);
    appendIntIfPresent(
      formData,
      'country_of_origin_id',
      form.countryOfOriginId
    );

    formData.append('rfid_chip_number', cleanText(form.rfidChipNumber));
    formData.append('gender', form.gender);
    formData.append('date_of_birth', cleanText(form.dateOfBirth));
    formData.append('registration_number', cleanText(form.registrationNumber));
    formData.append('registration_date', cleanText(form.registrationDate));
    formData.append('pedigree_html', cleanText(form.pedigreeHtml));

    // الصورة
    if (newImage instanceof File) {
      formData.append('image', newImage);
    } else if (removeExistingImage && horse?.image) {
      formData.append('delete_image', 'true');
    }

    // شهادة التسجيل
    if (newCert instanceof File) {
      formData.append('registration_certificate', newCert);
    } else if (removeExistingCert && horse?.registration_certificate) {
      formData.append('delete_registration_certificate', 'true');
    }
    // ملاحظة: modified_by يُشتق في الخادم من التوكن (JWT) ولا يُرسل من هنا

    try {
      await api.put(`/horses/${horseId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(t('horse_updated_successfully'));
      setError('');
      setTimeout(() => navigate('/horse-list'), 900);
    } catch (err) {
      const msgKey =
        err?.response?.data?.message || err?.response?.data?.error;
      setError(msgKey ? t(msgKey) : t('error_updating_horse'));
      setSuccess('');
      setSubmitting(false);
    }
  };

  const genderOptions = [
    { value: 'S', label: t('horse_stallion') },
    { value: 'M', label: t('mare') },
    { value: 'G', label: t('gelding') },
  ];

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{ py: 3 }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <SettingsPageHeader
        title={t('edit_horse')}
        subtitle={form.name || t('horse_singular')}
        backTo="/horse-list"
      />

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* القسم 1: البيانات الأساسية */}
        <SectionCard title={t('basic_information')}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label={t('horse_name')}
              value={form.name}
              onChange={setField('name')}
              error={Boolean(fieldErrors.name)}
              helperText={fieldErrors.name || ''}
              autoFocus
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label={t('horse_name_arabic')}
              value={form.nameArabic}
              onChange={setField('nameArabic')}
              error={Boolean(fieldErrors.nameArabic)}
              helperText={fieldErrors.nameArabic || ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required error={Boolean(fieldErrors.gender)}>
              <InputLabel id="gender-label">{t('gender')}</InputLabel>
              <Select
                labelId="gender-label"
                id="gender"
                value={form.gender}
                label={t('gender')}
                onChange={setField('gender')}
              >
                {genderOptions.map((g) => (
                  <MenuItem key={g.value} value={g.value}>
                    {g.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label={t('date_of_birth')}
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.dateOfBirth}
              onChange={setField('dateOfBirth')}
              error={Boolean(fieldErrors.dateOfBirth)}
              helperText={fieldErrors.dateOfBirth || ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TypeaheadSelect
              label={t('breed')}
              value={form.breedId}
              onChange={(val) => setForm((p) => ({ ...p, breedId: val === '' ? '' : Number(val) }))}
              options={breeds.map((breed) => ({ value: breed.breed_id, label: `${breed.breed_english} / ${breed.breed_arabic}` }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TypeaheadSelect
              label={t('color_id')}
              value={form.colorId}
              onChange={(val) => setForm((p) => ({ ...p, colorId: val === '' ? '' : Number(val) }))}
              options={colors.map((color) => ({ value: color.color_id, label: `${color.color_english} / ${color.color_arabic}` }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('rfid_chip_number')}
              value={form.rfidChipNumber}
              onChange={setField('rfidChipNumber')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TypeaheadSelect
              label={t('owner_id')}
              value={form.ownerId}
              onChange={(val) => setForm((p) => ({ ...p, ownerId: val === '' ? '' : Number(val) }))}
              options={owners.map((owner) => ({ value: owner.owner_id, label: owner.name }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="countryOfOriginId-label">
                {t('country_of_origin')}
              </InputLabel>
              <Select
                labelId="countryOfOriginId-label"
                id="countryOfOriginId"
                value={
                  countries.find(
                    (c) => c.country_id === form.countryOfOriginId
                  )
                    ? form.countryOfOriginId
                    : ''
                }
                label={t('country_of_origin')}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    countryOfOriginId:
                      e.target.value === '' ? '' : Number(e.target.value),
                  }))
                }
              >
                {countries.map((country) => (
                  <MenuItem key={country.country_id} value={country.country_id}>
                    <CountryFlagLabel
                      country={
                        isRtl
                          ? country.country_arabic
                          : country.country_english
                      }
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </SectionCard>

        {/* القسم 2: النسب */}
        <SectionCard title={t('pedigree_information')}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('father_name')}
              value={form.fatherName}
              onChange={setField('fatherName')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('father_name_arabic')}
              value={form.fatherNameArabic}
              onChange={setField('fatherNameArabic')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('mother_name')}
              value={form.motherName}
              onChange={setField('motherName')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('mother_name_arabic')}
              value={form.motherNameArabic}
              onChange={setField('motherNameArabic')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('breeder_name')}
              value={form.breederName}
              onChange={setField('breederName')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('breeder_name_arabic')}
              value={form.breederNameArabic}
              onChange={setField('breederNameArabic')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('pedigree_html')}
              multiline
              rows={4}
              value={form.pedigreeHtml}
              onChange={setField('pedigreeHtml')}
            />
          </Grid>
        </SectionCard>

        {/* القسم 3: التسجيل */}
        <SectionCard title={t('registration_information')}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('registration_number')}
              value={form.registrationNumber}
              onChange={setField('registrationNumber')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('registration_date')}
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.registrationDate}
              onChange={setField('registrationDate')}
            />
          </Grid>
        </SectionCard>

        {/* القسم 4: المرفقات */}
        <SectionCard title={t('attachments')}>
          {/* صورة الحصان */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ color: COLORS.muted, mb: 1 }}>
              {t('horse_image')}
            </Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{ borderColor: COLORS.line, color: COLORS.text }}
            >
              {t('upload_horse_image')}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  setNewImage(e.target.files[0] || null);
                  setRemoveExistingImage(false);
                }}
              />
            </Button>

            {/* معاينة الصورة الجديدة */}
            {newImage && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ color: COLORS.green, fontWeight: 700, mb: 0.5 }}>
                  {t('new_image_selected')}
                </Typography>
                <Box
                  component="img"
                  src={newImagePreviewUrl}
                  alt={t('image_preview')}
                  sx={{
                    width: 160,
                    height: 'auto',
                    borderRadius: 1,
                    border: `1px solid ${COLORS.line}`,
                    display: 'block',
                  }}
                />
                <Button
                  size="small"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => setNewImage(null)}
                  sx={{ mt: 0.5 }}
                >
                  {t('remove_selected')}
                </Button>
              </Box>
            )}

            {/* الصورة الحالية المخزّنة */}
            {!newImage && horse?.image && !removeExistingImage && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ color: COLORS.muted, mb: 0.5 }}>
                  {t('current_horse_image')}
                </Typography>
                <Box
                  component="img"
                  src={`${horse.image}`}
                  alt="Horse"
                  sx={{
                    width: 160,
                    height: 'auto',
                    borderRadius: 1,
                    border: `1px solid ${COLORS.line}`,
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <Button
                  size="small"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => setRemoveExistingImage(true)}
                  sx={{ mt: 0.5 }}
                >
                  {t('delete_image')}
                </Button>
              </Box>
            )}
          </Grid>

          {/* شهادة التسجيل */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ color: COLORS.muted, mb: 1 }}>
              {t('upload_registration_certificate')} *
            </Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              color={fieldErrors.registrationCertificate ? 'error' : 'primary'}
              sx={{
                borderColor: fieldErrors.registrationCertificate
                  ? undefined
                  : COLORS.line,
                color: fieldErrors.registrationCertificate
                  ? undefined
                  : COLORS.text,
              }}
            >
              {t('upload_registration_certificate')}
              <input
                type="file"
                hidden
                accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={(e) => {
                  setNewCert(e.target.files[0] || null);
                  setRemoveExistingCert(false);
                }}
              />
            </Button>

            {/* شهادة جديدة مختارة */}
            {newCert && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ color: COLORS.green, fontWeight: 700 }}>
                  {t('new_certificate_selected')}: {newCert.name}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => setNewCert(null)}
                  sx={{ mt: 0.5 }}
                >
                  {t('remove_selected')}
                </Button>
              </Box>
            )}

            {/* الشهادة الحالية المخزّنة */}
            {!newCert &&
              horse?.registration_certificate &&
              !removeExistingCert && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" sx={{ color: COLORS.muted, mb: 0.5 }}>
                    {t('current_registration_certificate')}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    href={horse.registration_certificate}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<OpenInNewIcon />}
                  >
                    {t('view_registration_certificate')}
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<CloseIcon />}
                    onClick={() => setRemoveExistingCert(true)}
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {t('delete_certificate')}
                  </Button>
                </Box>
              )}

            {fieldErrors.registrationCertificate && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {fieldErrors.registrationCertificate}
              </Typography>
            )}
          </Grid>
        </SectionCard>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{
              flex: 1,
              bgcolor: COLORS.gold,
              '&:hover': { bgcolor: COLORS.goldHover },
            }}
            startIcon={
              submitting ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {submitting ? t('saving') : t('edit_horse')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            disabled={submitting}
            onClick={() => navigate('/horse-list')}
            sx={{ flex: 1 }}
          >
            {t('cancel')}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={2500}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default HorseEdit;
